import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Crown, 
  Heart, 
  Shield, 
  Brain, 
  Lightbulb, 
  Users,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Quote
} from 'lucide-react';

const features = [
  {
    icon: Crown,
    title: "Adjust My Crown",
    description: "AI-powered support when you want to text him, feel triggered, or doubt yourself.",
    color: "from-amber-400 to-amber-600"
  },
  {
    icon: Heart,
    title: "Trauma Bond Breaker",
    description: "No-contact tracker, urge interruption tools, and breadcrumb detection.",
    color: "from-rose-400 to-rose-600"
  },
  {
    icon: Lightbulb,
    title: "Gaslighting Recovery",
    description: "'Was it real?' clarity worksheets and cognitive reframing exercises.",
    color: "from-purple-400 to-purple-600"
  },
  {
    icon: Shield,
    title: "Boundary Builder",
    description: "Script templates, rehearsal prompts, and guilt-dismantling exercises.",
    color: "from-emerald-400 to-emerald-600"
  },
  {
    icon: Brain,
    title: "Nervous System Reset",
    description: "60-second grounding, breathwork, and panic-to-peace protocols.",
    color: "from-blue-400 to-blue-600"
  },
  {
    icon: Users,
    title: "Sisterhood Community",
    description: "Prayer wall and support from women on the same healing journey.",
    color: "from-pink-400 to-pink-600"
  }
];

const testimonials = [
  {
    quote: "This app helped me realize I wasn't crazy. The 'Adjust My Crown' feature saved me from breaking no-contact so many times.",
    name: "Sarah M.",
    detail: "2 weeks in recovery"
  },
  {
    quote: "The daily declarations ground me in truth every morning. I finally feel like myself again.",
    name: "Grace T.",
    detail: "Healing journey"
  },
  {
    quote: "Having Scripture woven into recovery content makes all the difference. This isn't just psychology—it's restoration.",
    name: "Michelle R.",
    detail: "Crown Premium member"
  }
];

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    navigate('/dashboard');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back, Queen!');
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success('Welcome to your healing journey!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Hero Section */}
      <header className="relative">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1737816473126-192c432b7e71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxldGhlcmVhbCUyMGdvbGQlMjBjcm93biUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzEwODk5MTh8MA&ixlib=rb-4.1.0&q=85"
            alt="Crown aesthetic"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background"></div>
        </div>

        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center animate-pulse-gold">
              <Crown className="w-6 h-6 text-purple-950" />
            </div>
            <span className="font-serif text-xl font-semibold">Reclaiming My Crown</span>
          </div>
          <Button 
            data-testid="get-started-btn"
            onClick={() => setAuthOpen(true)}
            className="rounded-full px-6 bg-primary hover:bg-primary/90"
          >
            Get Started
          </Button>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-primary text-sm font-medium mb-6 animate-fade-in">
              <Sparkles className="w-4 h-4" />
              Faith-Based Recovery for Women
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 animate-slide-up">
              You Are Not Broken.
              <br />
              <span className="text-primary">You Are Rebuilding.</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 animate-slide-up stagger-1 max-w-2xl">
              A structured, Christ-centered recovery space for women healing from narcissistic abuse. 
              Identity restoration. Boundary formation. Peace cultivation.
            </p>
            <div className="flex flex-wrap gap-4 animate-slide-up stagger-2">
              <Button 
                data-testid="begin-healing-btn"
                onClick={() => setAuthOpen(true)}
                className="btn-gold rounded-full px-8 py-6 text-lg font-semibold transition-all"
              >
                Begin Your Healing Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/about')}
                className="rounded-full px-8 py-6 text-lg border-2 border-primary text-primary hover:bg-primary/5"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features-section" className="py-20 px-6 bg-purple-50/50 dark:bg-purple-950/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Recovery with Refinement
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Not dramatic. Not reactive. Not bitter. Grounded. Educated. Restorative. Kingdom-centered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title}
                  onClick={() => setAuthOpen(true)}
                  className="crown-card p-6 hover:border-amber-400/50 transition-all duration-300 hover:-translate-y-1 animate-slide-up cursor-pointer"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  data-testid={`feature-card-${idx}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Signature Feature */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-6">
                <Crown className="w-4 h-4" />
                Signature Feature
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-6">
                "Adjust My Crown"
              </h2>
              <p className="text-muted-foreground mb-6">
                When you want to text him, feel triggered, doubt yourself, miss him, or question if you overreacted—tap 
                <strong className="text-foreground"> Adjust My Crown </strong> 
                and receive:
              </p>
              <ul className="space-y-4">
                {[
                  "Truth about manipulation patterns",
                  "A reminder of who you are in Christ",
                  "A loving correction ('Missing chaos doesn't mean it was healthy.')",
                  "A grounded action step"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 font-scripture text-lg italic text-primary">
                Identity → Clarity → Authority → Peace
              </p>
            </div>
            <div className="relative">
              <div className="glass-gold rounded-3xl p-8 animate-pulse-gold">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full gold-gradient flex items-center justify-center">
                    <Crown className="w-7 h-7 text-purple-950" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Adjust My Crown</p>
                    <p className="text-sm text-muted-foreground">AI-Powered Support</p>
                  </div>
                </div>
                <div className="space-y-4 text-sm">
                  <div className="bg-white/60 dark:bg-black/20 rounded-xl p-4">
                    <p className="font-medium text-amber-700 dark:text-amber-300 mb-1">Truth</p>
                    <p className="text-foreground">Reaching out breaks the healing cycle. Narcissists use any contact as an opportunity to re-engage manipulation tactics.</p>
                  </div>
                  <div className="bg-white/60 dark:bg-black/20 rounded-xl p-4">
                    <p className="font-medium text-purple-600 mb-1">Identity Reminder</p>
                    <p className="text-foreground">You are a daughter of the King (Galatians 3:26). You don't need validation from someone who couldn't see your worth.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-purple-50/50 dark:bg-purple-950/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Sisters Who Healed
            </h2>
            <p className="text-muted-foreground">Stories from women reclaiming their crowns</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx}
                className="bg-white dark:bg-purple-950/50 border border-purple-100 dark:border-purple-800/30 rounded-2xl p-6 relative"
                data-testid={`testimonial-${idx}`}
              >
                <Quote className="w-8 h-8 text-purple-200 dark:text-purple-800 absolute top-6 right-6" />
                <p className="font-scripture text-lg italic text-foreground mb-6 relative z-10">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="royal-gradient rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <img 
                src="https://images.unsplash.com/photo-1689080999707-8d981b2665ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjYWxtJTIwd29tYW4lMjBuYXR1cmUlMjByZWNvdmVyeXxlbnwwfHx8fDE3NzEwODk5MTl8MA&ixlib=rb-4.1.0&q=85"
                alt="Calm"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <Crown className="w-16 h-16 mx-auto mb-6 text-amber-300" />
              <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
                Ready to Reclaim Your Crown?
              </h2>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Start your faith-based recovery journey today. Free tier available.
              </p>
              <Button 
                data-testid="cta-start-btn"
                onClick={() => setAuthOpen(true)}
                className="btn-gold rounded-full px-8 py-6 text-lg"
              >
                Start Free Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-purple-100 dark:border-purple-800/30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            <span className="text-sm text-muted-foreground">© 2026 Reclaiming My Crown</span>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Grounded. Educated. Restorative. Kingdom-centered.
          </p>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 font-serif text-2xl">
              <Crown className="w-8 h-8 text-amber-500" />
              {isLogin ? 'Welcome Back, Queen' : 'Begin Your Journey'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit}>
              <TabsContent value="register" className="space-y-4">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input 
                    id="name"
                    data-testid="register-name-input"
                    placeholder="How should we call you?"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={!isLogin}
                    className="mt-1"
                  />
                </div>
              </TabsContent>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email"
                    type="email"
                    data-testid="email-input"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    id="password"
                    type="password"
                    data-testid="password-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="auth-submit-btn"
                  className="w-full rounded-full py-6 bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
                </Button>
              </div>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
